import dayjs from '~/timeInit';

import { VendorPeriodSummary, VendorPromotion, VendorSummaryComparison } from '~/types/services/vendor';

import { convertValueByFX } from '~/utils/convertValueByFX';
import { formatCurrency } from '~/utils/currencyFormatter';
import { formatNumber, formatPercentChange } from '~/utils/numberFormatters';

import {
  currentPeriodLabel,
  granularityForPeriod,
  periodLabel,
  quarterShort,
  rollingShort,
  trailingAvgLabel,
} from './periodUtils';

// Visual treatment per Kieran's original Slack mockup:
//   - emoji prefix on each metric heading + the two section headings
//   - light-horizontal divider lines (─, U+2500) wrapping each section heading
//   - ✅ marker on improvement deltas (positive % for additive metrics,
//     "improved by" lines on Search rank where lower = better)
// Section headings are UPPERCASE to match the mockup; period suffix is uppercased too.
const DIVIDER = '─'.repeat(31);

const METRIC_EMOJI: Record<string, string> = {
  'Total Sales': '💰',
  ADR: '💵',
  'Room nights': '🛏️',
  LOS: '📅',
  'Lead time': '⏱️',
  Impressions: '👁️',
  'Search rank': '🔍',
  'Direct Rate Share vs 3rd Parties': '💼',
};

function decoratedHeading(emoji: string, text: string): Array<string> {
  return [DIVIDER, `${emoji} ${text}`, DIVIDER];
}

function appendImprovementMarker(line: string, isImprovement: boolean): string {
  return isImprovement ? `${line} ✅` : line;
}

// Unicode bullet (•, U+2022) rather than "*" — rich-text email composers don't
// auto-convert "*" into a bullet glyph, so the recipient would see a literal
// asterisk. Indentation matches KP's Slack mockup: 3 chars for top-level, 5 for
// sub-info (comparison) lines that qualify the baseline above.
//
// Indent uses non-breaking space (U+00A0) instead of regular space so the
// whitespace survives paste into HTML-aware composers (Gmail, Outlook web)
// where the default CSS whitespace-collapse rule would otherwise eat regular
// leading spaces. NBSP renders identically to space but isn't collapsible.
// CAUTION: the value of NBSP below is U+00A0 NON-BREAKING SPACE, written here as the
// \u00A0 escape so it is visible in source. Do NOT "simplify" this to a regular space (U+0020) — that would defeat the whole point
// (regular leading whitespace gets collapsed when pasted into Gmail/Outlook's HTML composer).
const NBSP = '\u00A0';
export const BULLET = `${NBSP.repeat(3)}• `;
export const SUB_BULLET = `${NBSP.repeat(5)}• `;

type MetricFormatter = (value: number) => string;

// Total Sales is stored in AUD by the BE (gross_sales_aud columns). When the Market
// Manager toggles to the vendor's local currency we convert client-side at the live
// rate (indicative only — historical sales are valued at today's FX). Always render
// with the dollar-type prefix (A$, S$, US$, …) so the currency is unambiguous.
function makeSalesFormatter(displayCurrency: string, fxRates: Record<string, number>): MetricFormatter {
  return (audValue: number) => {
    if (displayCurrency === 'AUD') {
      return formatCurrency(audValue, 'AUD', 'AU', { format: 'roundedDollar' }) ?? '';
    }
    try {
      const converted = convertValueByFX({
        fxRates: { ...fxRates, AUD: 1 },
        fromCurrency: 'AUD',
        toCurrency: displayCurrency,
        value: audValue,
      });
      return formatCurrency(converted, displayCurrency, 'AU', { format: 'roundedDollar' }) ?? '';
    } catch {
      // Unknown currency / missing rate — fall back to AUD rather than crash the email.
      return formatCurrency(audValue, 'AUD', 'AU', { format: 'roundedDollar' }) ?? '';
    }
  };
}

type MonthlyLabels = {
  granularity: 'month';
  avg: string; // e.g. "12 month avg"
  current: string; // "last month"
  perPeriod: string; // "per month"
};

// Quarterly and rolling (half-year) share this shape (current/trailing/vs-avg/LY/vs-LY);
// only the copy differs, so one type + rendering function set serves both.
type PeriodicLabels = {
  granularity: 'quarter' | 'half_year';
  current: string; // "Q2" or "Mar–Sep"
  trailingAvg: string; // "Last 4Q Avg" or "Last 4H Avg"
  vsAvg: string; // "vs. Average"
  ly: string; // "Same Q last year" or "Same period last year"
  vsLy: string; // "vs. LY"
  perPeriod: string; // "per quarter" or "per 6 months"
};

type LabelContext = MonthlyLabels | PeriodicLabels;

function metricBlockMonthly(
  label: string,
  comparison: VendorSummaryComparison,
  formatter: MetricFormatter,
  labels: MonthlyLabels,
  unitSuffix = '',
  includePerPeriodOnAvg = false,
): Array<string> | null {
  const withSuffix = (formatted: string) => (unitSuffix ? `${formatted} ${unitSuffix}` : formatted);
  const lines: Array<string> = [];

  if (comparison.trailing_avg != null) {
    const trailing = includePerPeriodOnAvg ? ` ${labels.perPeriod}` : '';
    lines.push(`${BULLET}${labels.avg}: ${withSuffix(formatter(comparison.trailing_avg))}${trailing}`);
  }
  if (comparison.current != null) {
    lines.push(`${BULLET}${labels.current}: ${withSuffix(formatter(comparison.current))}`);
  }
  if (comparison.percent_change != null) {
    // Indent the comparison line so it reads as sub-info to the baseline above.
    const line = `${SUB_BULLET}% change vs ${labels.avg}: ${formatPercentChange(comparison.percent_change)}`;
    lines.push(appendImprovementMarker(line, comparison.percent_change > 0));
  }

  if (lines.length === 0) {
    return null;
  }
  return [`${METRIC_EMOJI[label] ?? ''} ${label}`.trim(), ...lines];
}

// Quarterly (and rolling half-year) block follows KP's prescribed structure:
//   * Q2: $744,600
//   * Last 4Q Avg: $710,124 per quarter
//   * vs. Average: +4.9%
//   * Same Q last year: $...
//   * vs. LY: +x.x%
// Avoid calling either delta "QoQ" — neither matches Tableau KTR's sequential prior-period
// definition. Intentional and stakeholder-confirmed (XTRNT-774); applies to rolling too.
function metricBlockPeriodic(
  label: string,
  comparison: VendorSummaryComparison,
  formatter: MetricFormatter,
  labels: PeriodicLabels,
  unitSuffix = '',
  includePerPeriodOnAvg = false,
): Array<string> | null {
  const withSuffix = (formatted: string) => (unitSuffix ? `${formatted} ${unitSuffix}` : formatted);
  const lines: Array<string> = [];

  if (comparison.current != null) {
    lines.push(`${BULLET}${labels.current}: ${withSuffix(formatter(comparison.current))}`);
  }
  if (comparison.trailing_avg != null) {
    const trailing = includePerPeriodOnAvg ? ` ${labels.perPeriod}` : '';
    lines.push(`${BULLET}${labels.trailingAvg}: ${withSuffix(formatter(comparison.trailing_avg))}${trailing}`);
  }
  // Indent the comparison lines so they read as sub-info to the baseline above.
  if (comparison.percent_change != null) {
    const line = `${SUB_BULLET}${labels.vsAvg}: ${formatPercentChange(comparison.percent_change)}`;
    lines.push(appendImprovementMarker(line, comparison.percent_change > 0));
  }
  if (comparison.same_period_last_year != null) {
    lines.push(`${BULLET}${labels.ly}: ${withSuffix(formatter(comparison.same_period_last_year))}`);
  }
  if (comparison.percent_change_vs_last_year != null) {
    const line = `${SUB_BULLET}${labels.vsLy}: ${formatPercentChange(comparison.percent_change_vs_last_year)}`;
    lines.push(appendImprovementMarker(line, comparison.percent_change_vs_last_year > 0));
  }

  if (lines.length === 0) {
    return null;
  }
  return [`${METRIC_EMOJI[label] ?? ''} ${label}`.trim(), ...lines];
}

function metricBlock(
  label: string,
  comparison: VendorSummaryComparison,
  formatter: MetricFormatter,
  labels: LabelContext,
  unitSuffix = '',
  includePerPeriodOnAvg = false,
): Array<string> | null {
  if (labels.granularity === 'month') {
    return metricBlockMonthly(label, comparison, formatter, labels, unitSuffix, includePerPeriodOnAvg);
  }
  return metricBlockPeriodic(label, comparison, formatter, labels, unitSuffix, includePerPeriodOnAvg);
}

function searchRankBlockMonthly(comparison: VendorSummaryComparison, labels: MonthlyLabels): Array<string> | null {
  const { current, trailing_avg } = comparison;
  const lines: Array<string> = [];

  if (trailing_avg != null) {
    lines.push(`${BULLET}${labels.avg}: ${formatNumber(trailing_avg, 1)}`);
  }
  if (current != null) {
    lines.push(`${BULLET}${labels.current}: ${formatNumber(current, 1)}`);
  }
  if (current != null && trailing_avg != null) {
    lines.push(`${SUB_BULLET}${positionDelta(current, trailing_avg, labels.avg)}`);
  }

  if (lines.length === 0) {
    return null;
  }
  return [`${METRIC_EMOJI['Search rank']} Search rank (lower = better)`, ...lines];
}

function searchRankBlockPeriodic(comparison: VendorSummaryComparison, labels: PeriodicLabels): Array<string> | null {
  const { current, trailing_avg, same_period_last_year } = comparison;
  const lines: Array<string> = [];

  if (current != null) {
    lines.push(`${BULLET}${labels.current}: ${formatNumber(current, 1)}`);
  }
  if (trailing_avg != null) {
    lines.push(`${BULLET}${labels.trailingAvg}: ${formatNumber(trailing_avg, 1)}`);
  }
  if (current != null && trailing_avg != null) {
    lines.push(`${SUB_BULLET}${positionDelta(current, trailing_avg, labels.trailingAvg)}`);
  }
  if (same_period_last_year != null) {
    lines.push(`${BULLET}${labels.ly}: ${formatNumber(same_period_last_year, 1)}`);
  }
  if (current != null && same_period_last_year != null) {
    lines.push(`${SUB_BULLET}${positionDelta(current, same_period_last_year, 'LY')}`);
  }

  if (lines.length === 0) {
    return null;
  }
  return [`${METRIC_EMOJI['Search rank']} Search rank (lower = better)`, ...lines];
}

// Directionality is communicated by the verb (improved/declined) plus the
// "(lower = better)" qualifier on the Search rank section heading — don't repeat
// the qualifier on every delta line, it gets noisy once both trailing-avg + LY render.
function positionDelta(current: number, baseline: number, baselineLabel: string): string {
  const delta = baseline - current;
  const magnitude = formatNumber(Math.abs(delta), 1);
  if (Math.abs(delta) < 0.05) {
    return `unchanged vs ${baselineLabel}`;
  }
  if (delta > 0) {
    return `improved by ${magnitude} positions vs ${baselineLabel} ✅`;
  }
  return `declined by ${magnitude} positions vs ${baselineLabel}`;
}

export type BuildEmailInput = {
  summary: VendorPeriodSummary;
  vendorName: string;
  // Vendor's billing currency (App.Vendor.currencyisocode). Used to format fixed-amount
  // promotion discounts in the right currency — TTV / sales metrics stay AUD because the
  // BE normalises them to AUD upstream (lpc_gross_sales_aud / gross_sales_aud columns).
  // Default to AUD if missing so we don't crash on a malformed vendor record.
  vendorCurrency?: string;
  promotions?: Array<VendorPromotion>;
  // Currency the Total Sales figures should be rendered in. Defaults to AUD (the BE's
  // native unit). When set to a vendor currency, `fxRates` must contain its AUD-relative
  // rate or the formatter silently falls back to AUD.
  displayCurrency?: string;
  // AUD-relative FX rates keyed by currency code (as returned by svc-fx). Only consulted
  // when `displayCurrency` differs from AUD.
  fxRates?: Record<string, number>;
};

export function buildVendorPerformanceEmail({
  summary,
  vendorName,
  vendorCurrency = 'AUD',
  promotions = [],
  displayCurrency = 'AUD',
  fxRates = {},
}: BuildEmailInput): string {
  const { performance } = summary;
  const wireGranularity = granularityForPeriod(summary.period);
  const labelText = periodLabel(summary.period, wireGranularity);
  const cadenceWord =
    wireGranularity === 'month' ? 'monthly' : wireGranularity === 'quarter' ? 'quarterly' : 'six-monthly';

  const labels: LabelContext =
    wireGranularity === 'quarter'
      ? {
          granularity: 'quarter',
          current: quarterShort(summary.period),
          trailingAvg: 'Last 4Q Avg',
          vsAvg: 'vs. Average',
          ly: 'Same Q last year',
          vsLy: 'vs. LY',
          perPeriod: 'per quarter',
        }
      : wireGranularity === 'rolling'
        ? {
            granularity: 'half_year',
            current: rollingShort(summary.period),
            trailingAvg: 'Last 4H Avg', // "H" = half-year period, not a calendar label
            vsAvg: 'vs. Average',
            ly: 'Same period last year',
            vsLy: 'vs. LY',
            perPeriod: 'per 6 months',
          }
        : {
            granularity: 'month',
            avg: trailingAvgLabel(wireGranularity, performance.ttv.periods_in_avg),
            current: currentPeriodLabel(wireGranularity),
            perPeriod: 'per month',
          };

  const salesFormatter = makeSalesFormatter(displayCurrency, fxRates);

  const metricBlocks = [
    metricBlock('Total Sales', performance.ttv, salesFormatter, labels, '', true),
    performance.ttv_adr ? metricBlock('ADR', performance.ttv_adr, salesFormatter, labels) : null,
    metricBlock('Room nights', performance.room_nights, (v) => formatNumber(v), labels, '', true),
    metricBlock('LOS', performance.los, (v) => formatNumber(v, 1), labels, 'nights'),
    metricBlock('Lead time', performance.lead_time, (v) => formatNumber(v, 0), labels, 'days'),
    metricBlock('Impressions', performance.impressions, (v) => formatNumber(v), labels),
    labels.granularity === 'month'
      ? searchRankBlockMonthly(performance.search_rank, labels)
      : searchRankBlockPeriodic(performance.search_rank, labels),
    metricBlock('Direct Rate Share vs 3rd Parties', performance.lpc_share, (v) => `${(v * 100).toFixed(1)}%`, labels),
  ].filter((block): block is Array<string> => block !== null);

  const performanceSection: Array<string> =
    metricBlocks.length > 0
      ? [
          ...decoratedHeading('📊', `PERFORMANCE METRICS — ${labelText.toUpperCase()}`),
          '',
          ...metricBlocks.flatMap((block, i) => (i === 0 ? block : ['', ...block])),
          '',
        ]
      : [];

  const upcomingPeriodWord =
    wireGranularity === 'month' ? 'month' : wireGranularity === 'quarter' ? 'quarter' : 'six months';

  return [
    `Subject: Your ${cadenceWord} Dynamic Connection Performance snapshot — ${labelText}`,
    '',
    'Hi team,',
    '',
    `Sharing your latest performance report from Luxury Escapes for ${vendorName} (${labelText}). We would love to book in a call to run through the data together and align on priorities for the coming ${upcomingPeriodWord}. Please reply with a couple of times that work for you.`,
    '',
    ...performanceSection,
    ...buildPromotionsSection(promotions, vendorCurrency),
    ...buildPropertyInsightsSection(summary),
    'If anything here prompts questions, we are happy to walk through it live.',
    '',
    'Best regards,',
    '[Market Manager name]',
    'LE Partner Team',
  ].join('\n');
}

function formatPromotionDateRange(promotion: VendorPromotion): string {
  const start = dayjs(promotion.start_date).format('D MMM YYYY');
  const end = dayjs(promotion.end_date).format('D MMM YYYY');
  return `${start}-${end}`;
}

function formatPromotionDiscount(promotion: VendorPromotion, vendorCurrency: string): string | null {
  if (promotion.discount_amount == null) {
    return null;
  }
  const amount =
    typeof promotion.discount_amount === 'string' ? Number(promotion.discount_amount) : promotion.discount_amount;
  if (!Number.isFinite(amount)) {
    return null;
  }
  if (promotion.discount_type === 'percentage') {
    return `Discount: ${formatNumber(amount, 0)}%`;
  }
  if (promotion.discount_type === 'fixed') {
    // 'roundedDollar' adds the country prefix on dollar-family currencies (A$, S$, US$, NZ$, …)
    // so the recipient can disambiguate at a glance. Non-dollar currencies (IDR Rp, JPY ¥,
    // EUR €, GBP £) already have unique symbols so the prefix logic is a no-op there.
    // Region arg follows the codebase convention (`'en-AU'` everywhere); see
    // BedbankPricingSummary / Item for precedent.
    const formatted = formatCurrency(amount, vendorCurrency, 'en-AU', { format: 'roundedDollar' });
    return `Discount: ${formatted ?? ''}`;
  }
  return `Discount: ${formatNumber(amount, 0)} ${promotion.discount_type ?? ''}`.trim();
}

function buildPromotionsSection(promotions: Array<VendorPromotion>, vendorCurrency: string): Array<string> {
  if (!promotions.length) {
    return [];
  }
  const bullets = promotions.map((promotion) => {
    const segments: Array<string> = [];
    const discount = formatPromotionDiscount(promotion, vendorCurrency);
    if (discount) {
      segments.push(discount);
    }
    segments.push(formatPromotionDateRange(promotion));
    const regionCount = promotion.discount_regions?.length ?? 0;
    if (regionCount > 0) {
      segments.push(`${regionCount} ${regionCount === 1 ? 'region' : 'regions'}`);
    }
    return `${BULLET}${segments.join('; ')}`;
  });
  return ['Promotions', ...bullets, ''];
}

function avgPriceDiffPercent(value: number): number {
  return Number(Math.abs(value * 100).toFixed(1));
}

function formatAvgPriceDiffWhenOverpriced(percent: number): string {
  return `When LE was overpriced, rates were ${percent.toFixed(1)}% higher than competitors on average`;
}

// Parity Score mirrors the Extranet "Offer page view parity" health figure, which clones the
// AP Price Parrot parity dashboard's formula: round((1 − out-of-parity rate) × 100), clamped
// 0–100. Kept byte-for-byte identical to SummaryTiles.tsx (chain parity dashboard) so the two
// can't drift — `overpriced` here is the impression-weighted rate_parity_overpriced_rate, which
// plays the role of `pctOop` there. Per XTRNT-796 the per-property bullet now reports only this
// score — the raw impressions count and the (redundant, inverse) overpriced % were dropped.
// Properties arrive pre-sorted by impressions DESC from the BE, so the top-5 slice still
// surfaces the vendor's most-visible properties.
function buildPropertyInsightsSection(summary: VendorPeriodSummary): Array<string> {
  const avgPriceDiff = summary.performance.avg_price_diff_when_le_overpriced?.current;
  const avgPriceDiffPct = avgPriceDiff == null ? null : avgPriceDiffPercent(avgPriceDiff);
  const avgPriceDiffBullet =
    avgPriceDiff == null || avgPriceDiff >= 0 || avgPriceDiffPct === 0
      ? null
      : `${BULLET}${formatAvgPriceDiffWhenOverpriced(avgPriceDiffPct)}`;

  const bullets = summary.properties
    .slice(0, 5)
    .map((property) => {
      const impressions = property.impressions.current;
      const overpriced = property.rate_parity_overpriced_rate.current;

      // Skip rows with no real visibility (also drops the empty duplicate rows the BE
      // occasionally emits for whitespace-variant property names) or no parity data.
      if (!property.property_name || impressions == null || impressions <= 0 || overpriced == null) {
        return null;
      }

      const parityScore = Math.max(0, Math.min(100, Math.round((1 - overpriced) * 100)));
      return `${BULLET}${property.property_name.trim()}: ${parityScore}% parity score`;
    })
    .filter((bullet): bullet is string => bullet !== null);

  const insightBullets = avgPriceDiffBullet == null ? bullets : [avgPriceDiffBullet, ...bullets];

  if (insightBullets.length === 0) {
    return [];
  }
  return [...decoratedHeading('🔎', 'PARITY & SEARCH INSIGHTS'), '', ...insightBullets, ''];
}
